/* ShishaGlass design system: shared motion + commerce helpers. No dependencies.
   Hooks (all opt-in via data attributes):
     [data-sg-reveal]            fade/rise in when scrolled into view ("pop", "zoom" variants)
     [data-sg-stagger]           children with [data-sg-reveal] get an increasing delay
     [data-sg-split]             headline revealed word by word
     [data-sg-parallax="0.15"]   moves with scroll (positive = slower than page)
     [data-sg-depth="12"]        follows the pointer inside [data-sg-depth-scope]
     [data-sg-tilt]              3D tilt toward the pointer
     [data-sg-carousel]          scroll-snap row with [data-sg-prev]/[data-sg-next]/[data-sg-progress]
     [data-sg-fit]               text scaled to fill its parent's width
     [data-sg-add="variantId"]   quick add to cart (+ toast, cart count update)
   Re-initialises inside a section when the theme editor reloads it, and tears
   down that section's listeners when it is unloaded. */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  /* Per-section teardown: listeners on window/document are registered with an
     AbortSignal tied to the element's section, so an editor reload or unload
     doesn't leave them running against detached DOM. */
  var controllers = new WeakMap();
  function signalFor(el) {
    var section = el.closest('.shopify-section') || document.body;
    var ctrl = controllers.get(section);
    if (!ctrl) { ctrl = new AbortController(); controllers.set(section, ctrl); }
    return ctrl.signal;
  }
  function teardown(section) {
    var ctrl = controllers.get(section);
    if (ctrl) { ctrl.abort(); controllers.delete(section); }
  }

  /* ---------- Reveal ----------
     Slides inside a carousel track reveal together when the track comes into
     view; observing each slide would leave off-screen (sideways) slides
     invisible until swiped. */
  var revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      if (el.hasAttribute('data-sg-track')) {
        el.querySelectorAll('[data-sg-reveal]').forEach(function (child) { child.classList.add('is-in'); });
      } else {
        el.classList.add('is-in');
      }
      revealObserver.unobserve(el);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }) : null;

  function initReveal(root) {
    root.querySelectorAll('[data-sg-stagger]').forEach(function (group) {
      var i = 0;
      group.querySelectorAll('[data-sg-reveal]').forEach(function (el) {
        if (el.closest('[data-sg-stagger]') === group) el.style.setProperty('--sg-i', i++);
      });
    });
    root.querySelectorAll('[data-sg-reveal], [data-sg-split]').forEach(function (el) {
      if (el.classList.contains('is-in')) return;
      if (!revealObserver || reduce.matches) { el.classList.add('is-in'); return; }
      var track = el.closest('[data-sg-track]');
      revealObserver.observe(track || el);
    });
  }

  /* ---------- Split headline into words (keeps <em>/<strong>/<br>) ---------- */
  function splitWords(el) {
    if (el.dataset.sgSplitDone) return;
    el.dataset.sgSplitDone = '1';
    var index = 0;
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 3) {
          var parts = child.textContent.split(/(\s+)/);
          var frag = document.createDocumentFragment();
          parts.forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
            var word = document.createElement('span');
            word.className = 'sg-word';
            var inner = document.createElement('span');
            inner.className = 'sg-word__inner';
            inner.style.setProperty('--sg-w', index++);
            inner.textContent = part;
            word.appendChild(inner);
            frag.appendChild(word);
          });
          child.parentNode.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          walk(child);
        }
      });
    })(el);
    el.setAttribute('aria-label', el.textContent.replace(/\s+/g, ' ').trim());
  }

  function initSplit(root) {
    root.querySelectorAll('[data-sg-split]').forEach(splitWords);
  }

  /* ---------- Scroll parallax ---------- */
  var parallaxItems = [];
  var parallaxTicking = false;

  function updateParallax() {
    parallaxTicking = false;
    var vh = window.innerHeight;
    // Drop items whose section was removed in the theme editor
    parallaxItems = parallaxItems.filter(function (item) { return item.el.isConnected; });
    // Read every box first, then write, so the frame never forces layout twice
    var reads = parallaxItems.map(function (item) { return item.box.getBoundingClientRect(); });
    parallaxItems.forEach(function (item, i) {
      var rect = reads[i];
      if (rect.bottom < -200 || rect.top > vh + 200) return;
      var progress = (rect.top + rect.height / 2 - vh / 2) / vh; // -1 .. 1 around centre
      item.el.style.setProperty('--sg-py', (progress * item.speed * vh * -0.5).toFixed(1) + 'px');
      if (item.rotate) item.el.style.setProperty('--sg-pr', (progress * item.rotate).toFixed(2) + 'deg');
    });
  }

  function requestParallax() {
    if (!parallaxTicking && parallaxItems.length) { parallaxTicking = true; window.requestAnimationFrame(updateParallax); }
  }

  function initParallax(root) {
    if (reduce.matches) return;
    root.querySelectorAll('[data-sg-parallax]').forEach(function (el) {
      if (el.dataset.sgParallaxDone) return;
      el.dataset.sgParallaxDone = '1';
      parallaxItems.push({
        el: el,
        box: el.closest('[data-sg-parallax-box]') || el,
        speed: parseFloat(el.getAttribute('data-sg-parallax')) || 0.15,
        rotate: parseFloat(el.getAttribute('data-sg-parallax-rotate')) || 0
      });
    });
    requestParallax();
  }

  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax);

  /* ---------- Pointer depth (hero collage) ---------- */
  function initDepth(root) {
    if (reduce.matches || !finePointer.matches) return;
    root.querySelectorAll('[data-sg-depth-scope]').forEach(function (scope) {
      if (scope.dataset.sgDepthDone) return;
      scope.dataset.sgDepthDone = '1';
      var layers = scope.querySelectorAll('[data-sg-depth]');
      var frame = null;
      var rect = null;
      scope.addEventListener('pointerenter', function () { rect = scope.getBoundingClientRect(); });
      scope.addEventListener('pointermove', function (e) {
        if (!rect) rect = scope.getBoundingClientRect();
        var x = (e.clientX - rect.left) / rect.width - 0.5;
        var y = (e.clientY - rect.top) / rect.height - 0.5;
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(function () {
          layers.forEach(function (layer) {
            var d = parseFloat(layer.getAttribute('data-sg-depth')) || 10;
            layer.style.setProperty('--sg-dx', (x * d).toFixed(1) + 'px');
            layer.style.setProperty('--sg-dy', (y * d).toFixed(1) + 'px');
          });
        });
      });
      scope.addEventListener('pointerleave', function () {
        rect = null;
        layers.forEach(function (layer) {
          layer.style.setProperty('--sg-dx', '0px');
          layer.style.setProperty('--sg-dy', '0px');
        });
      });
    });
  }

  /* ---------- Tilt ---------- */
  function initTilt(root) {
    if (reduce.matches || !finePointer.matches) return;
    root.querySelectorAll('[data-sg-tilt]').forEach(function (el) {
      if (el.dataset.sgTiltDone) return;
      el.dataset.sgTiltDone = '1';
      var max = parseFloat(el.getAttribute('data-sg-tilt')) || 7;
      var rect = null;
      el.addEventListener('pointerenter', function () { rect = el.getBoundingClientRect(); });
      el.addEventListener('pointermove', function (e) {
        if (!rect) rect = el.getBoundingClientRect();
        var x = (e.clientX - rect.left) / rect.width - 0.5;
        var y = (e.clientY - rect.top) / rect.height - 0.5;
        el.style.transform = 'perspective(900px) rotateX(' + (-y * max).toFixed(2) + 'deg) rotateY(' + (x * max).toFixed(2) + 'deg)';
      });
      el.addEventListener('pointerleave', function () { rect = null; el.style.transform = ''; });
    });
  }

  /* ---------- Carousel ---------- */
  function initCarousels(root) {
    root.querySelectorAll('[data-sg-carousel]').forEach(function (wrap) {
      if (wrap.dataset.sgCarouselDone) return;
      wrap.dataset.sgCarouselDone = '1';
      var track = wrap.querySelector('[data-sg-track]');
      if (!track) return;
      var signal = signalFor(wrap);
      var prev = wrap.querySelector('[data-sg-prev]');
      var next = wrap.querySelector('[data-sg-next]');
      var bar = wrap.querySelector('[data-sg-progress]');
      var focusMode = wrap.hasAttribute('data-sg-focus');

      function gap() { return parseFloat(getComputedStyle(track).columnGap) || 0; }

      function step() {
        var slide = track.firstElementChild;
        if (!slide) return track.clientWidth * 0.8;
        if (focusMode) return slide.offsetWidth + gap();
        var perView = Math.max(1, Math.floor((track.clientWidth + gap()) / (slide.offsetWidth + gap())));
        return (slide.offsetWidth + gap()) * perView;
      }

      // Focus carousels: the slide nearest the centre gets .is-focus
      function updateFocus() {
        var centre = track.getBoundingClientRect().left + track.clientWidth / 2;
        var best = null, bestDist = Infinity;
        Array.prototype.forEach.call(track.children, function (slide) {
          var r = slide.getBoundingClientRect();
          var d = Math.abs(r.left + r.width / 2 - centre);
          if (d < bestDist) { bestDist = d; best = slide; }
        });
        Array.prototype.forEach.call(track.children, function (slide) {
          slide.classList.toggle('is-focus', slide === best);
        });
      }

      var queued = false;
      function update() {
        queued = false;
        if (focusMode) updateFocus();
        var max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        if (bar) {
          bar.style.setProperty('--sg-bar-size', Math.min(1, track.clientWidth / track.scrollWidth));
          bar.style.setProperty('--sg-bar-pos', max > 0 ? track.scrollLeft / max : 0);
        }
      }
      function queueUpdate() {
        if (!queued) { queued = true; window.requestAnimationFrame(update); }
      }

      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', queueUpdate, { passive: true });
      window.addEventListener('resize', queueUpdate, { signal: signal });

      // Drag to scroll with a mouse; touch keeps native swiping
      var dragging = false, startX = 0, startLeft = 0, moved = false;
      function endDrag() {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
      }
      // Stop the browser's own link/image drag, which would cancel ours
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        dragging = true; moved = false; startX = e.clientX; startLeft = track.scrollLeft;
      });
      window.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 4) { moved = true; track.classList.add('is-dragging'); }
        if (moved) track.scrollLeft = startLeft - dx;
      }, { signal: signal });
      window.addEventListener('pointerup', endDrag, { signal: signal });
      window.addEventListener('pointercancel', endDrag, { signal: signal });
      // A drag shouldn't count as a click on the card underneath
      track.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);

      update();
    });
  }

  /* ---------- Fit text to its container width ([data-sg-fit]) ---------- */
  var fitItems = [];
  function fitAll() {
    fitItems = fitItems.filter(function (el) { return el.isConnected; });
    fitItems.forEach(function (el) {
      var box = el.parentElement;
      var cs = getComputedStyle(box);
      var avail = box.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      var range = document.createRange();
      range.selectNodeContents(el);
      var current = parseFloat(getComputedStyle(el).fontSize);
      var width = range.getBoundingClientRect().width;
      if (width > 0 && avail > 0) el.style.fontSize = Math.floor(current * avail / width * 0.995) + 'px';
    });
  }
  function initFit(root) {
    root.querySelectorAll('[data-sg-fit]').forEach(function (el) {
      if (fitItems.indexOf(el) === -1) fitItems.push(el);
    });
    fitAll();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitAll);
  }
  window.addEventListener('resize', function () { requestAnimationFrame(fitAll); });

  /* ---------- Toast ---------- */
  var toast, toastTimer;
  // action: omitted → "View cart" link; false → no link; { label, href } → that link
  function showToast(message, isError, action) {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'sg-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML = '<span class="sg-toast__text"></span><a class="sg-btn sg-btn--ink sg-btn--sm" href="' + rootUrl + 'cart">View cart</a>';
      document.body.appendChild(toast);
    }
    toast.querySelector('.sg-toast__text').textContent = message;
    toast.classList.toggle('is-error', !!isError);
    var link = toast.querySelector('a');
    var custom = action && action.href;
    link.textContent = custom ? action.label : 'View cart';
    link.href = custom ? action.href : rootUrl + 'cart';
    link.hidden = !!isError || action === false;
    // next frame so the slide-in transition runs
    requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.classList.remove('is-visible'); }, 4200);
  }

  /* ---------- Quick add ---------- */
  function updateCartCount(count) {
    document.querySelectorAll('.bigcounter, [data-sg-cart-count]').forEach(function (el) {
      el.textContent = count;
      if (el.hasAttribute('data-sg-cart-count')) el.hidden = !(count > 0);
    });
    // Keep the cart link's accessible name in step with the badge
    document.querySelectorAll('[data-sg-cart-label]').forEach(function (el) {
      el.setAttribute('aria-label', 'Cart, ' + count + ' ' + (count === 1 ? 'item' : 'items'));
    });
  }

  function readJson(response) {
    return response.json().catch(function () { return {}; }).then(function (data) {
      return { ok: response.ok, data: data };
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-sg-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var id = btn.getAttribute('data-sg-add');
    var title = btn.getAttribute('data-sg-title') || 'Item';
    btn.disabled = true;
    btn.classList.add('is-loading');
    fetch(rootUrl + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(readJson)
      .then(function (res) {
        if (!res.ok) throw new Error(res.data.description || res.data.message || 'Sorry, that couldn’t be added to your cart.');
        // The cart drawer (assets/sg-cart-drawer.js) shows the result when present
        if (window.SGCart) document.dispatchEvent(new CustomEvent('sg:cart:added', { detail: res.data }));
        else showToast(title + ' added to your cart');
        return fetch(rootUrl + 'cart.js').then(readJson);
      })
      .then(function (res) { if (res && res.ok) updateCartCount(res.data.item_count); })
      .catch(function (err) {
        showToast(err && err.message && !/JSON|fetch/i.test(err.message) ? err.message : 'Sorry, that couldn’t be added to your cart.', true);
      })
      .then(function () { btn.disabled = false; btn.classList.remove('is-loading'); });
  });

  /* ---------- Back-to-top (#scrollUp from the legacy plugin) ----------
     The plugin creates a bare <a href="#top"> with an icon only; give it an
     accessible name once it exists. */
  function labelScrollUp() {
    var b = document.getElementById('scrollUp');
    if (!b) return false;
    b.setAttribute('aria-label', 'Back to top');
    b.setAttribute('title', 'Back to top');
    return true;
  }
  if (!labelScrollUp()) {
    var tries = 0, t = setInterval(function () { if (labelScrollUp() || ++tries > 20) clearInterval(t); }, 500);
  }

  /* ---------- Init + theme editor ---------- */
  function init(root) {
    initSplit(root);
    initReveal(root);
    initParallax(root);
    initDepth(root);
    initTilt(root);
    initCarousels(root);
    initFit(root);
  }

  window.SG = { init: init, toast: showToast };

  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
    // Editor: show everything immediately instead of waiting for scroll
    e.target.querySelectorAll('[data-sg-reveal], [data-sg-split]').forEach(function (el) { el.classList.add('is-in'); });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
})();
